import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import DevelopmentMandateForm from '@/components/DevelopmentMandateForm';
import {
  EMPTY_DEVELOPMENT_MANDATE,
  type DevelopmentMandateSelections,
} from '@/config/developmentMandate';
import { createProject } from '@/services/projectService';
import type { ScriptProject } from '@/types/scriptProject';

type StartProjectDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (project: ScriptProject) => void;
};

export default function StartProjectDialog({
  open,
  onOpenChange,
  onCreated,
}: StartProjectDialogProps) {
  const [name, setName] = useState('');
  const [mandate, setMandate] = useState<DevelopmentMandateSelections>({
    ...EMPTY_DEVELOPMENT_MANDATE,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setMandate({ ...EMPTY_DEVELOPMENT_MANDATE });
    setError(null);
    setSaving(false);
  };

  const handleClose = (next: boolean) => {
    if (saving) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Project name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const project = await createProject({
        name: trimmed,
        development_mandate: mandate,
      });
      onCreated(project);
      reset();
      onOpenChange(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#141414] border-[#2a2a2a] text-white sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-sans font-bold uppercase tracking-wide">
            Create development mandate
          </DialogTitle>
          <DialogDescription className="text-white/50 font-sans text-sm mt-2">
            Name the mandate and configure scope parameters. Empty sections inherit account defaults
            from Settings.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label htmlFor="project-name" className="ni-label">
              Mandate name
            </label>
            <input
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-2 w-full rounded-sm border border-[#3a3a3a] bg-black px-3 py-2 text-white font-sans focus:outline-none focus:ring-1 focus:ring-white/30"
              placeholder="e.g. Premium Limited Drama Series"
            />
          </div>
          <div className="ni-panel p-4">
            <DevelopmentMandateForm value={mandate} onChange={setMandate} compact />
          </div>
          {error && <p className="text-sm text-red-400 font-sans">{error}</p>}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="indexOutline" onClick={() => handleClose(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="indexPrimary" onClick={() => void handleSubmit()} disabled={saving}>
            {saving ? 'Creating…' : 'Create mandate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
